import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link2, Users, Mail, ChevronDown, Check } from "lucide-react";
import type { AudienceMode } from "@core/types/poll";
import { GroupSelector } from "../groups/GroupSelector";
import type { PollData } from "@core/hooks/usePolls";

interface AudienceChipProps {
  mode: AudienceMode;
  onModeChange: (mode: AudienceMode) => void;
  /** Emails courants (source de vérité = participantEmails du PollCreator). */
  emails: string;
  onEmailsChange: (value: string) => void;
  groupId?: string;
  groupName?: string;
  onGroupSelect: (
    emails: string[],
    group?: { id: string; name: string },
  ) => void;
  currentPollData?: PollData;
}

type OptionMeta = {
  value: AudienceMode;
  icon: React.ReactNode;
  title: string;
  desc: string;
};

const emailList = (raw: string): string[] =>
  raw
    .split(/[,;\n]+/)
    .map((e) => e.trim())
    .filter(Boolean);

// Neutral elevation shadow (black, not theme-tinted) -- drop shadows read as
// depth in both light and dark themes, unlike the primary-tinted glow above.
const AUDIENCE_POPOVER_CLASS =
  "absolute left-0 right-0 top-[calc(100%+8px)] z-50 rounded-xl border border-border bg-popover p-1.5 shadow-[0_20px_50px_rgba(0,0,0,0.55)]"; // color-exempt

/**
 * Contrôle "Audience" up-front (chip + popover), au-dessus du calendrier.
 * Sélectionne le mode de partage (lien / groupe / e-mails directs) et, pour
 * groupe/e-mails, révèle le détail correspondant. Écrit dans le même état que
 * le chemin de publication (participantEmails + audienceMode/GroupId/GroupName).
 */
export function AudienceChip({
  mode,
  onModeChange,
  emails,
  onEmailsChange,
  groupId,
  groupName,
  onGroupSelect,
  currentPollData,
}: AudienceChipProps) {
  const { t } = useTranslation(["creator"]);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [open]);

  const options: OptionMeta[] = [
    {
      value: "link",
      icon: <Link2 className="w-4 h-4 text-green-400" />, // color-exempt
      title: t("creator:intent.audience_link_title"),
      desc: t("creator:intent.audience_link_desc"),
    },
    {
      value: "group",
      icon: <Users className="w-4 h-4 text-purple-400" />, // color-exempt
      title: t("creator:intent.audience_group_title"),
      desc: t("creator:intent.audience_group_desc"),
    },
    {
      value: "emails",
      icon: <Mail className="w-4 h-4 text-blue-400" />, // color-exempt
      title: t("creator:intent.audience_email_title"),
      desc: t("creator:intent.audience_email_desc"),
    },
  ];

  const current = options.find((o) => o.value === mode) ?? options[0];
  const isGroup = mode === "group";

  // Résumé du chip pour le mode groupe : "Groupe · <nom>" / "Liste · N répondants".
  const chipTitle =
    isGroup && groupName
      ? t("creator:intent.audience_group_selected_title", { name: groupName })
      : current.title;
  const chipDesc =
    isGroup && groupName
      ? t("creator:intent.audience_group_selected_desc", {
          count: emailList(emails).length,
        })
      : current.desc;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t("creator:intent.cap_audience")}
        </span>
      </div>

      <div className="relative" ref={wrapRef}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          data-testid="audience-chip"
          className={`w-full flex h-[62px] items-center gap-3 rounded-xl border bg-muted px-3.5 text-left transition-colors ${
            isGroup
              ? "border-primary/40 shadow-[0_0_0_1px_hsl(var(--primary)/0.24)]"
              : "border-border hover:border-input"
          }`}
        >
          <span className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-lg bg-accent">
            {current.icon}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-foreground">
              {chipTitle}
            </span>
            <span className="block truncate text-[11.5px] text-muted-foreground">
              {chipDesc}
            </span>
          </span>
          <ChevronDown className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
        </button>

        {open && (
          <div
            className={AUDIENCE_POPOVER_CLASS}
            data-testid="audience-popover"
            onClick={(e) => e.stopPropagation()}
          >
            {options.map((opt) => {
              const selected = opt.value === mode;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onModeChange(opt.value);
                    setOpen(false);
                  }}
                  data-testid={`audience-option-${opt.value}`}
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
                    selected ? "bg-accent" : "hover:bg-accent"
                  }`}
                >
                  <span className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-lg bg-muted">
                    {opt.icon}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-foreground">
                      {opt.title}
                    </span>
                    <span className="block text-[11.5px] text-muted-foreground">
                      {opt.desc}
                    </span>
                  </span>
                  <span
                    className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border ${
                      selected ? "border-primary bg-primary" : "border-input"
                    }`}
                  >
                    {selected && (
                      <Check className="h-3 w-3 text-primary-foreground" />
                    )}
                  </span>
                </button>
              );
            })}
            <p className="border-t border-border px-3 pb-1.5 pt-2 text-[11px] text-muted-foreground">
              {t("creator:intent.popover_note")}
            </p>
          </div>
        )}
      </div>

      {/* Détail contextuel — réutilise le plumbing existant (GroupSelector, emails). */}
      {isGroup && (
        <div className="mt-3">
          <GroupSelector
            currentEmails={emailList(emails)}
            selectedGroupId={groupId}
            onSelectGroup={onGroupSelect}
            currentPollData={currentPollData}
          />
        </div>
      )}

      {mode === "emails" && (
        <div className="mt-3 space-y-1.5">
          <textarea
            rows={3}
            value={emails}
            onChange={(e) => onEmailsChange(e.target.value)}
            placeholder="email1@example.com, email2@example.com"
            data-testid="audience-emails-input"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      )}
    </div>
  );
}

export default AudienceChip;
